import { useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import { ACTIONS } from "@/auth";
import { AppHeader } from "@app/components/accounting-approval/app-header";
import { Sidebar } from "@app/components/app-layout/sidebar";
import { AccessDeniedNotice } from "@/components/auth-guard/access-denied-notice";
import { useAccessCountdown } from "@/components/auth-guard/access-countdown-context";
import { useCan } from "@/hooks/use-can";
import { useIsMobile } from "@app/hooks/use-is-mobile";
import { VIEW_TO_ROUTE } from "@app/constants/navigation-routes";
import { VIEW } from "@/constants/tabs";
import type { ActiveView } from "@/constants/tabs";

export function AppLayout() {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const { isSigningOut, secondsRemaining, message } = useAccessCountdown();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    const effectiveSidebarCollapsed = !isMobile && isSidebarCollapsed;

    const canDashboard = useCan(ACTIONS.dashboard.read);
    const canUsers = useCan(ACTIONS.users.read);
    const canGroups = useCan(ACTIONS.groups.read);
    const canPermissions = useCan(ACTIONS.permissions.read);
    const canPolicyExceptions = useCan(ACTIONS.policy.exceptions.read);
    const canPolicyMileage = useCan(ACTIONS.policy.mileage.read);
    const canPolicyCurrency = useCan(ACTIONS.policy.currency.read);
    const canPolicyHotel = useCan(ACTIONS.policy.hotel.read);
    const canPolicyReceipt = useCan(ACTIONS.policy.receipt.read);
    const canApprovals = useCan(ACTIONS.manage.approvals.read);
    const canCategories = useCan(ACTIONS.manage.categories.read);
    const canUtilities = useCan(ACTIONS.utilities.read);

    const currentAllowedViews = new Set<ActiveView>([
        ...(canDashboard ? [VIEW.Dashboard] : []),
        ...(canUsers ? [VIEW.Users] : []),
        ...(canGroups ? [VIEW.Groups] : []),
        ...(canPermissions ? [VIEW.Permissions] : []),
        ...(canPolicyExceptions ? [VIEW.PolicyExceptions] : []),
        ...(canPolicyMileage ? [VIEW.PolicyMileage] : []),
        ...(canPolicyCurrency ? [VIEW.PolicyCurrency] : []),
        ...(canPolicyHotel ? [VIEW.PolicyHotel] : []),
        ...(canPolicyReceipt ? [VIEW.PolicyReceipt] : []),
        ...(canApprovals ? [VIEW.Approval] : []),
        ...(canCategories ? [VIEW.Categories] : []),
    ]);

    // Keep the last authorized nav so the sidebar shows the same items (just
    // disabled) while signing out, instead of emptying as permissions clear.
    const lastAuthorizedViews = useRef<Set<ActiveView>>(currentAllowedViews);
    const lastShowUtilities = useRef<boolean>(canUtilities);
    if (!isSigningOut) {
        lastAuthorizedViews.current = currentAllowedViews;
        lastShowUtilities.current = canUtilities;
    }
    const allowedViews = isSigningOut ? lastAuthorizedViews.current : currentAllowedViews;
    const showUtilities = isSigningOut ? lastShowUtilities.current : canUtilities;

    const handleSidebarToggle = () => {
        if (isMobile) {
            setIsMobileSidebarOpen(false);
            return;
        }
        setIsSidebarCollapsed((prev) => !prev);
    };

    return (
        <div className="relative flex h-screen overflow-hidden overflow-x-hidden bg-white">
            {isMobile && isMobileSidebarOpen && (
                <button type="button" aria-label="Close menu" className="fixed inset-0 z-40 bg-black/30" onClick={() => setIsMobileSidebarOpen(false)} />
            )}

            <Sidebar
                isCollapsed={effectiveSidebarCollapsed}
                isMobile={isMobile}
                isMobileOpen={isMobileSidebarOpen}
                allowedViews={allowedViews}
                showUtilities={showUtilities}
                disabled={isSigningOut}
                onToggle={handleSidebarToggle}
                onViewChange={(view) => {
                    navigate(VIEW_TO_ROUTE[view]);
                    if (isMobile) setIsMobileSidebarOpen(false);
                }}
            />

            <div className="flex min-w-0 flex-1 flex-col overflow-hidden overflow-x-hidden">
                <AppHeader onOpenMobileMenu={() => setIsMobileSidebarOpen(true)} />

                <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-3 sm:p-6">
                    {isSigningOut ? <AccessDeniedNotice message={message} secondsRemaining={secondsRemaining} /> : <Outlet />}
                </main>
            </div>
        </div>
    );
}
